import React, { useRef, useEffect } from 'react';

const TrailPreview: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;
    let containerRadius = 150; // Will be updated in resize

    const ballColors = ['#00e5ff', '#d500f9', '#ffea00', '#ff0080', '#00ff00', '#ff9100'];
    const ballColor = ballColors[Math.floor(Math.random() * ballColors.length)];

    // Simulation State
    // Start with random velocity to ensure variety
    let ball = { 
      x: 0, 
      y: 0, 
      vx: (Math.random() < 0.5 ? -1 : 1) * (4 + Math.random() * 4), 
      vy: -5, 
      radius: 6, 
      color: ballColor 
    };
    
    interface Point { x: number, y: number }
    let trail: Point[] = [];
    const trailLength = 100;

    const resize = () => {
      if (containerRef.current && canvas) {
        const { clientWidth, clientHeight } = containerRef.current;
        width = clientWidth;
        height = clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        centerX = width / 2;
        centerY = height / 2;
        
        // Dynamic radius: standardized to 0.8
        containerRadius = (Math.min(width, height) / 2) * 0.8;
      }
    };

    window.addEventListener('resize', resize);
    resize();

    const update = () => {
      const gravity = 0.25;
      const friction = 1.0; 

      ball.vy += gravity;
      ball.vx *= friction;
      ball.vy *= friction;
      
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
      const maxSpeed = 12; 
      if (speed > maxSpeed) {
        ball.vx = (ball.vx / speed) * maxSpeed;
        ball.vy = (ball.vy / speed) * maxSpeed;
      }

      ball.x += ball.vx;
      ball.y += ball.vy;

      // Update Trail
      trail.unshift({ x: ball.x, y: ball.y });
      if (trail.length > trailLength) {
        trail.pop();
      }

      // Wall Collision
      const dist = Math.sqrt(ball.x * ball.x + ball.y * ball.y);
      if (dist + ball.radius > containerRadius) {
        const nx = ball.x / dist;
        const ny = ball.y / dist;
        
        // Reflect vector
        const dot = ball.vx * nx + ball.vy * ny;
        ball.vx = (ball.vx - 2 * dot * nx);
        ball.vy = (ball.vy - 2 * dot * ny);

        // Randomize on bounce
        const jitter = 2.0;
        ball.vx += (Math.random() - 0.5) * jitter;
        ball.vy += (Math.random() - 0.5) * jitter;

        // Physics: Push out of wall
        const overlap = dist + ball.radius - containerRadius;
        ball.x -= nx * overlap;
        ball.y -= ny * overlap;

        const currentSpeed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
        const targetSpeed = 10;
        ball.vx = (ball.vx / currentSpeed) * targetSpeed;
        ball.vy = (ball.vy / currentSpeed) * targetSpeed;
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      
      ctx.save();
      ctx.translate(centerX, centerY);

      // Draw Container with vibrant gradient
      ctx.beginPath();
      ctx.arc(0, 0, containerRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-containerRadius, -containerRadius, containerRadius, containerRadius);
      grad.addColorStop(0, '#ff0080');
      grad.addColorStop(0.5, '#00e5ff');
      grad.addColorStop(1, '#ffea00');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw Trail
      if (trail.length > 1) {
        for (let i = 0; i < trail.length - 1; i++) {
          const p1 = trail[i];
          const p2 = trail[i+1];
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          
          const alpha = (1 - (i / trailLength)) * 0.6;
          ctx.strokeStyle = ball.color.replace(')', '').replace('rgb', 'rgba') + `, ${alpha})`;
          // Handle hex to rgba if needed, but for simplicity I'll just use ctx.globalAlpha
          ctx.globalAlpha = alpha;
          ctx.strokeStyle = ball.color;
          ctx.lineWidth = (1 - (i / trailLength)) * ball.radius * 2;
          ctx.lineCap = 'round';
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
      }

      // Draw Ball
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
      ctx.fillStyle = ball.color;
      ctx.fill();

      ctx.restore();
    };

    const loop = () => {
      update();
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="group relative rounded-[3rem] overflow-hidden aspect-square glass-pink border-none hover:glow-pink transition-all duration-500 shadow-2xl">
      <canvas ref={canvasRef} className="block" />
      
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white to-transparent pointer-events-none">
        <h3 className="text-black font-bold text-lg uppercase tracking-tight">TRAIL</h3>
        <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Persistence Mapping</p>
      </div>
    </div>
  );
};

export default TrailPreview;
